import {
  X,
  ShoppingBag,
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  Heart,
} from "lucide-react";
import "./CartDrawer.scss";
import { useCart } from "../../../../context/CartContext";
import { useWishlist } from "../../../../context/WishlistContext";
import { Link, useNavigate } from "react-router-dom";
import { getLanguage } from "../../../../utils/Helper";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import PaymentInstructionsModal from "../../paymentInstructionsModal/PaymentInstructionsModal";

const CartDrawer = () => {
  const {
    cartItems,
    isCartOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    subtotal,
    shipping = 0,
    total,
    cartCount,
    isLoading,
    initiateCheckout,
  } = useCart();

  const { addToWishlist } = useWishlist();

  const navigate = useNavigate();
  const lang = getLanguage();

  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [movingToWishlist, setMovingToWishlist] = useState(new Set());

  // Content translations
  const content = useMemo(
    () => ({
      EN: {
        processing: "Processing...",
        shoppingCart: "Shopping Cart",
        emptyCart: "Your cart is empty",
        emptyCartText: "Start adding products to your cart!",
        continueShopping: "Continue Shopping",
        clearAll: "Clear All",
        color: "Color",
        size: "Size",
        free: "FREE",
        freeShippingNotice: "Add {amount} more for free shipping!",
        subtotal: "Subtotal",
        tax: "Tax (14%)",
        shipping: "Shipping",
        proceedToCheckout: "Proceed to Checkout",
        withoutBox: "Without Box",
        total: "Total",
        originalPrice: "Original",
        onlyLeft: "Only {count} left in stock",
        remove: "Remove",
        moveToWishlist: "Move to Wishlist",
        quantity: "Quantity",
        price: "Price",
        save: "Save",
        discount: "Discount",
        add: "Add",
        more: "more",
        forFreeShipping: "for free shipping!",
        itemTotal: "Total",
        cartUpdated: "Cart Updated",
        availabilityIssue: "Some items have been updated:",
        updatedFrom: "Updated from",
        to: "to",
        available: "available",
        removedOutOfStock: "Removed - Out of Stock",
        tryAgain: "Try Checkout Again",
        dismiss: "Dismiss",
      },
      AR: {
        processing: "جاري المعالجة...",
        shoppingCart: "عربة التسوق",
        emptyCart: "عربة التسوق فارغة",
        emptyCartText: "ابدأ بإضافة المنتجات إلى عربة التسوق!",
        clearAll: "مسح الكل",
        color: "اللون",
        size: "المقاس",
        free: "مجاناً",
        freeShippingNotice: "أضف {amount} أخرى للحصول على توصيل مجاني!",
        subtotal: "المجموع الفرعي",
        tax: "الضريبة (14%)",
        shipping: "التوصيل",
        total: "الإجمالي",
        proceedToCheckout: "إتمام الشراء",
        continueShopping: "متابعة التسوق",
        withoutBox: "بدون علبة",
        originalPrice: "السعر الأصلي",
        onlyLeft: "بقي {count} فقط في المخزون",
        remove: "حذف",
        moveToWishlist: "نقل للمفضلة",
        quantity: "الكمية",
        price: "السعر",
        save: "وفر",
        discount: "خصم",
        add: "أضف",
        more: "أخرى",
        forFreeShipping: "للحصول على توصيل مجاني!",
        itemTotal: "الإجمالي",
        cartUpdated: "تم تحديث السلة",
        availabilityIssue: "تم تعديل بعض المنتجات:",
        updatedFrom: "تم التعديل من",
        to: "إلى",
        available: "متوفر",
        removedOutOfStock: "تم الإزالة - غير متوفر",
        tryAgain: "حاول مرة أخرى",
        dismiss: "إغلاق",
      },
    }),
    []
  );

  const currentContent = content[lang] || content.EN;

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    setIsCheckingOut(true);
    setAvailabilityError(null);

    try {
      const checkoutResult = await initiateCheckout();

      if (!checkoutResult.success) {
        // Handle unavailable items
        if (
          checkoutResult.unavailableItems &&
          checkoutResult.unavailableItems.length > 0
        ) {
          // Set error to display in cart
          setAvailabilityError({
            unavailableItems: checkoutResult.unavailableItems,
          });

          // Cart is already updated by the context
          // Force re-render by waiting a bit
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Show toast notification
          const lang = getLanguage();
          toast.warning(
            lang === "AR"
              ? "تم تحديث السلة. بعض المنتجات غير متوفرة بالكمية المطلوبة."
              : "Cart updated. Some items are not available in requested quantities.",
            {
              position: lang === "AR" ? "top-right" : "top-left",
              autoClose: 5000,
            }
          );

          return;
        } else if (checkoutResult.errorMessage) {
          setAvailabilityError({
            message: checkoutResult.errorMessage,
          });

          // Show error toast
          toast.error(checkoutResult.errorMessage, {
            position: lang === "AR" ? "top-right" : "top-left",
            autoClose: 5000,
          });
        }
        return;
      }

      // Success - Store checkout result and show payment modal
      setCheckoutResult(checkoutResult);
      // setShowPaymentModal(true);
      setShowPaymentModal(false);
      setAvailabilityError(null);
      closeCart();
      
      // Navigate to checkout page
      navigate("/checkout", {
        state: {
          checkoutId: checkoutResult.checkoutId,
          expiresAt: checkoutResult.expiresAt,
          expiresIn: checkoutResult.expiresIn,
        },
      });
      
    } catch (error) {
      console.error("Checkout error:", error);
      const lang = getLanguage();
      const errorMessage =
        lang === "AR"
          ? "حدث خطأ أثناء إتمام عملية الشراء. برجاء المحاولة مرة أخرى."
          : "An error occurred during checkout. Please try again.";

      setAvailabilityError({
        message: errorMessage,
      });

      toast.error(errorMessage, {
        position: lang === "AR" ? "top-right" : "top-left",
        autoClose: 5000,
      });
    } finally {
      setIsCheckingOut(false);
      setShowPaymentModal(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    // Don't navigate, let user stay in cart
  };

  // ✅ NEW: Move to Wishlist Handler
  const handleMoveToWishlist = async (item) => {
    const itemKey = `${item.product.id}-${item.variant.id}-${item.withoutBox}`;
    
    // Prevent multiple clicks
    if (movingToWishlist.has(itemKey)) return;
    
    setMovingToWishlist(prev => new Set(prev).add(itemKey));

    try {
      // Add to wishlist
      const result = await addToWishlist(item.variant.id);
      
      if (result.success) {
        // Remove from cart
        await removeFromCart(item.product.id, item.variant.id, item.withoutBox);
        
        toast.success(
          lang === "AR" 
            ? "❤️ تم النقل للمفضلة!" 
            : "❤️ Moved to wishlist!",
          {
            position: lang === "AR" ? "top-right" : "top-left",
            autoClose: 2000,
          }
        );
      }
    } catch (error) {
      console.error("Error moving to wishlist:", error);
      toast.error(
        lang === "AR" 
          ? "خطأ في نقل المنتج للمفضلة" 
          : "Error moving to wishlist",
        {
          position: lang === "AR" ? "top-right" : "top-left",
        }
      );
    } finally {
      setMovingToWishlist(prev => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
    }
  };

  // Disable checkout button during processing
  const isCheckoutDisabled =
    isLoading || isCheckingOut || cartItems.length === 0;

  const handleProductClick = (pro) => {
    const collectionName = (pro.collection?.nameEn ? pro.collection?.nameEn: pro.collection?.name_en)
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");
    const catName = (pro.category?.nameEn ? pro.category?.nameEn: pro.category?.name_en)
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");
    const subCatName = pro.subCategory
      ? (pro?.subCategory?.nameEn ? pro?.subCategory?.nameEn : pro?.subCategory?.name_en)
          ?.toLowerCase()
          .trim()
          .split(" ")
          .join("-")
      : "";
    return !subCatName
      ? `/collection/${collectionName}/${catName}/${pro.id}`
      : `/collection/${collectionName}/${catName}/${subCatName}/${pro.id}`;
  };

  // Format currency with locale
  const formatCurrency = (amount) => {
    if (typeof amount !== "number")
      return lang === "AR" ? "0.00 ج.م" : "EGP 0.00";
    return lang === "AR"
      ? `${amount.toFixed(2)} ج.م`
      : `EGP ${amount.toFixed(2)}`;
  };

  // Calculate discount percentage
  const calculateDiscountPercentage = (originalPrice, discountPrice) => {
    if (!discountPrice || !originalPrice || discountPrice >= originalPrice)
      return 0;
    return Math.round(((originalPrice - discountPrice) / originalPrice) * 100);
  };


  // Get Localized name based on language
  const getLocalizedName = (item) => {
    if (!item) return "";
    const nameEn= item?.nameEn? item?.nameEn: item?.name_en;
    const nameAr= item?.nameAr? item?.nameAr: item?.name_ar;
    return lang === "AR" && nameAr ? nameAr : nameEn;
  };

  // Get free shipping remaining amount
  const getFreeShippingRemaining = () => {
    const freeShippingThreshold = 2000;
    const remaining = freeShippingThreshold - subtotal;
    return remaining > 0 ? remaining : 0;
  };

  // Calculate price with without box discount
  const calculateItemPrice = (price, isWithoutBox) => {
    const basePrice = price || 0;
    return isWithoutBox ? Math.max(0, basePrice - 100) : basePrice;
  };

  // Calculate item total
  const calculateItemTotal = (price, quantity, isWithoutBox) => {
    const itemPrice = calculateItemPrice(price, isWithoutBox);
    return itemPrice * quantity || 0;
  };

  // Format free shipping notice
  const getFreeShippingNotice = () => {
    const remaining = getFreeShippingRemaining();
    if (remaining <= 0) return "";

    const formattedAmount = formatCurrency(remaining);
    return lang === "AR"
      ? `${currentContent.add} ${formattedAmount} ${currentContent.forFreeShipping}`
      : `${currentContent.add} ${formattedAmount} ${currentContent.forFreeShipping}`;
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`cart-overlay ${isCartOpen ? "active" : ""}`}
        onClick={closeCart}
      />

      {/* Cart Drawer */}
      <div
        className={`cart-drawer ${isCartOpen ? "open" : ""} ${
          lang === "AR" ? "rtl" : "ltr"
        }`}
      >
        <div className="cart-glass-container">
          {/* Loading Overlay */}
          {isLoading && (
            <div className="cart-loading-overlay">
              <div className="cart-loading-spinner">
                <Loader2 size={48} className="spinner-icon" />
                <p className="loading-text">{currentContent.processing}</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="cart-header">
            <div className="header-content">
              <div
                className={`header-title-section ${
                  lang === "AR" ? "flex-row-reverse" : ""
                }`}
              >
                <ShoppingBag size={24} className="cart-icon" />
                <h3 className="cart-title">{currentContent.shoppingCart}</h3>
                {cartCount > 0 && (
                  <span className="cart-count-badge">{cartCount}</span>
                )}
              </div>
              <button
                className="close-button"
                onClick={closeCart}
                disabled={isLoading}
                aria-label={lang === "AR" ? "إغلاق" : "Close"}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Cart Items */}
          <div className="cart-items-container">
            {cartItems.length === 0 ? (
              <div className="empty-cart">
                <div className="empty-cart-icon">
                  <ShoppingBag size={64} />
                </div>
                <h4 className="empty-cart-title">{currentContent.emptyCart}</h4>
                <p className="empty-cart-text">
                  {currentContent.emptyCartText}
                </p>
                <button
                  className="continue-shopping-btn"
                  onClick={closeCart}
                  disabled={isLoading}
                >
                  {currentContent.continueShopping}
                </button>
              </div>
            ) : (
              <>
                {/* Availability Error Card */}
                {availabilityError && (
                  <div className="availability-error-card">
                    <div className="error-card-header">
                      <AlertCircle size={24} className="error-icon" />
                      <h4 className="error-title">
                        {currentContent.cartUpdated}
                      </h4>
                    </div>
                    <p className="error-message">
                      {currentContent.availabilityIssue}
                    </p>

                    {availabilityError.unavailableItems && (
                      <div className="unavailable-items-list">
                        {availabilityError.unavailableItems.map(
                          (item, index) => {
                            const productName = item.productName;
                            const boxStatus = item.withoutBox
                              ? ` (${currentContent.withoutBox})`
                              : "";

                            return (
                              <div key={index} className="unavailable-item">
                                <div className="item-info">
                                  <span className="item-name-error">
                                    {productName}
                                    {boxStatus}
                                  </span>
                                  {item.newQuantity === 0 ? (
                                    <span className="item-status removed">
                                      {currentContent.removedOutOfStock}
                                    </span>
                                  ) : (
                                    <span className="item-status updated">
                                      {currentContent.updatedFrom}{" "}
                                      {item.oldQuantity} {currentContent.to}{" "}
                                      {item.newQuantity} (
                                      {currentContent.available})
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}

                    {availabilityError.message && (
                      <p className="error-general-message">
                        {availabilityError.message}
                      </p>
                    )}

                    <div className="error-card-actions">
                      <button
                        className="try-again-btn"
                        // onClick={handleCheckout}
                        onClick={()=> setShowPaymentModal(true)}
                        disabled={isCheckingOut || cartItems.length === 0}
                      >
                        {isCheckingOut ? (
                          <>
                            <Loader2 size={16} className="spinner-icon" />
                            {currentContent.processing}
                          </>
                        ) : (
                          <>
                            <CheckCircle size={16} />
                            {currentContent.tryAgain}
                          </>
                        )}
                      </button>
                      <button
                        className="dismiss-btn"
                        onClick={() => setAvailabilityError(null)}
                      >
                        {currentContent.dismiss}
                      </button>
                    </div>
                  </div>
                )}

                {/* Clear Cart Button */}
                <div className="clear-cart-section">
                  <button
                    className="clear-cart-btn"
                    onClick={clearCart}
                    disabled={isLoading}
                  >
                    <Trash2
                      size={16}
                      className={lang === "AR" ? "ms-1" : "me-1"}
                    />
                    {currentContent.clearAll}
                  </button>
                </div>

                {/* Items List */}
                <div className="cart-items-list">
                  {cartItems.map((item) => {
                    const price =
                      item.variant.discountPrice || item.variant.price;
                    const originalPrice = item.variant.price;
                    const hasDiscount =
                      item.variant.discountPrice &&
                      item.variant.discountPrice < item.variant.price;
                    const isWithoutBox = item.withoutBox || false;
                    const maxQuantity = isWithoutBox
                      ? (item.variant.withoutBoxQuantity) || 0
                      : (item.variant.quantity) || 0;
                    const discountPercentage = calculateDiscountPercentage(
                      originalPrice,
                      price
                    );

                    // Calculate prices with without box discount
                    const displayPrice = calculateItemPrice(
                      price,
                      isWithoutBox
                    );
                    const displayOriginalPrice = calculateItemPrice(
                      originalPrice,
                      isWithoutBox
                    );
                    const itemTotal = calculateItemTotal(
                      price,
                      item.quantity,
                      isWithoutBox
                    );
                    const showOriginalPrice = hasDiscount;
                    
                    // Check if currently moving to wishlist
                    const itemKey = `${item.product.id}-${item.variant.id}-${item.withoutBox}`;
                    const isMovingToWishlist = movingToWishlist.has(itemKey);

                    return (
                      <div
                        key={`${item.product.id}-${item.variant.id}-${
                          isWithoutBox ? "without" : "with"
                        }`}
                        className="cart-item"
                      >
                        <div className="item-image-container">
                          <img
                            src={item.product.images[0]?.image_url}
                            alt={getLocalizedName(item.product)}
                            className="item-image"
                          />
                          {(discountPercentage > 0 || isWithoutBox) && (
                            <span className="item-discount-badge">
                              {isWithoutBox
                                ? currentContent.withoutBox
                                : `-${discountPercentage}%`}
                            </span>
                          )}
                        </div>

                        <div className="item-details">
                          <div className="item-header" onClick={closeCart}>
                            <Link
                              to={handleProductClick(item.product)}
                              style={{ textDecoration: "none" }}
                            >
                              <h6 className="item-name clickable">
                                {getLocalizedName(item.product)}
                              </h6>
                            </Link>
                            {item.product.brand && (
                              <span className="item-brand">
                                {getLocalizedName(item.product.brand)}
                              </span>
                            )}
                          </div>

                          <div className="item-variant-info">
                            {item.variant.color?.id && (
                              <span className="variant-label">
                                <span
                                  className="color-dot"
                                  style={{
                                    backgroundColor: item.variant.color.hexCode,
                                  }}
                                />
                                {getLocalizedName(item.variant.color)}
                              </span>
                            )}
                            {item.variant.size?.id && (
                              <span className="variant-label">
                                {getLocalizedName(item.variant.size)}
                              </span>
                            )}
                          </div>

                          <div className="item-price-section">
                            <div className="price-group">
                              <span className="current-price">
                                {formatCurrency(displayPrice)}
                              </span>
                              {showOriginalPrice && (
                                <span className="original-price">
                                  {formatCurrency(displayOriginalPrice)}
                                </span>
                              )}
                            </div>
                            <span className="item-total">
                              {currentContent.itemTotal}:{" "}
                              {formatCurrency(itemTotal)}
                            </span>
                          </div>

                          <div
                            className={`item-actions ${
                              lang === "AR" ? "flex-row-reverse" : ""
                            }`}
                          >
                            {/* Quantity Controls */}
                            <div
                              className={`quantity-controls ${
                                lang === "AR" ? "flex-row-reverse" : ""
                              }`}
                            >
                              <button
                                className="qty-btn"
                                onClick={() =>
                                  updateQuantity(
                                    item.product.id,
                                    item.variant.id,
                                    item.quantity - 1,
                                    isWithoutBox
                                  )
                                }
                                disabled={isLoading || item.quantity <= 1}
                                aria-label={
                                  lang === "AR"
                                    ? "تقليل الكمية"
                                    : "Decrease quantity"
                                }
                              >
                                <Minus size={14} />
                              </button>
                              <span className="qty-value">{item.quantity}</span>
                              <button
                                className="qty-btn"
                                onClick={() =>
                                  updateQuantity(
                                    item.product.id,
                                    item.variant.id,
                                    item.quantity + 1,
                                    isWithoutBox
                                  )
                                }
                                disabled={
                                  isLoading || item.quantity >= maxQuantity
                                }
                                aria-label={
                                  lang === "AR"
                                    ? "زيادة الكمية"
                                    : "Increase quantity"
                                }
                              >
                                <Plus size={14} />
                              </button>
                            </div>

                            {/* ✅ NEW: Move to Wishlist Button */}
                            <button
                              className="wishlist-btn"
                              onClick={() => handleMoveToWishlist(item)}
                              disabled={isLoading || isMovingToWishlist}
                              aria-label={currentContent.moveToWishlist}
                              title={currentContent.moveToWishlist}
                            >
                              {isMovingToWishlist ? (
                                <Loader2 size={16} className="spinner-icon" />
                              ) : (
                                <Heart size={16} />
                              )}
                            </button>

                            {/* Remove Button */}
                            <button
                              className="remove-btn"
                              onClick={() =>
                                removeFromCart(
                                  item.product.id,
                                  item.variant.id,
                                  isWithoutBox
                                )
                              }
                              disabled={isLoading}
                              aria-label={currentContent.remove}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Cart Footer (Summary) */}
          {cartItems.length > 0 && (
            <div className="cart-footer">
              <div className="price-summary">
                {getFreeShippingRemaining() > 0 && (
                  <div className="free-shipping-notice">
                    <span>{getFreeShippingNotice()}</span>
                  </div>
                )}
                <div className="summary-divider" />
                <div className="summary-row total-row">
                  <span className="summary-label">{currentContent.total}</span>
                  <span className="summary-value total-value">
                    {formatCurrency(total)}
                  </span>
                </div>
              </div>

              <button
                className="checkout-btn"
                // onClick={handleCheckout}
                onClick={()=> setShowPaymentModal(true)}
                disabled={isCheckoutDisabled}
              >
                {isCheckingOut ? (
                  <>
                    <Loader2 size={20} className="spinner-icon" />
                    {currentContent.processing}
                  </>
                ) : (
                  <>
                    {currentContent.proceedToCheckout}
                    <ArrowRight
                      size={20}
                      className={
                        lang === "AR" ? "rotate-180 me-2 ms-0" : "ms-2"
                      }
                    />
                  </>
                )}
              </button>

              <button
                className="continue-shopping-link"
                onClick={closeCart}
                disabled={isLoading}
              >
                {currentContent.continueShopping}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Instructions Modal */}
      <PaymentInstructionsModal
        isOpen={showPaymentModal}
        onClose={handleClosePaymentModal}
        onProceed={handleCheckout}
        depositAmount={total}
      />
    </>
  );
};

export default CartDrawer;